import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { featureAvailability, type FeatureAvailability } from '../configuration/authorization';
import { featureConfigurationQueryOptions } from '../configuration/configuration-queries';
import { hasRole } from '../session/access';
import type { SessionContextValue } from '../session/session-context';
import { accessQueryOptions } from '../session/session-queries';
import type { Access, Identity, RoleType } from '../session/types';

/**
 * Route guards for `beforeLoad`, ported from portal-ui-lib's `authGuard`, `roleGuard`,
 * `globalAdminGuard` and `capabilityGuard`/`capabilitiesGuard`. See docs/dealer-portal-react/shell.md.
 *
 * They fail closed, like the Angular guards, and they wait rather than deny: a check that needs
 * `/me/access` or the feature configuration awaits it through the query cache instead of reading
 * whatever has loaded so far. The app renders its router only once the session is known, so
 * `status: 'pending'` never reaches a guard; if it did, it would be sent to log in.
 *
 * ```ts
 * export const Route = createFileRoute('/_app/leads')({
 *   staticData: { featureKey: 'leads' },
 *   beforeLoad: ({ context, location }) =>
 *     requireFeature(context, location, { feature: 'leads', permissions: ['lead:read'] }),
 * });
 * ```
 */

/** What every guard reads from the router context. */
export interface GuardContext {
  session: SessionContextValue;
  queryClient: QueryClient;
}

/** The part of TanStack's `location` a guard needs. */
export interface GuardLocation {
  href: string;
}

/** Where guards send people. The same paths in every portal. */
export const GUARD_PATHS = {
  login: '/login',
  unauthorized: '/unauthorized',
  unavailable: '/unavailable',
} as const;

/**
 * A `returnUrl` worth following: a path in this app. Anything with a scheme or a host
 * (`https://…`, `//evil.example`, `/\evil.example`) is refused, so the login page cannot be used
 * to bounce a user off-site.
 */
export function safeReturnUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return null;
  }
  if (value.startsWith('//') || value.startsWith('/\\')) {
    return null;
  }
  return value;
}

/** The login URL that brings the user back to `location` afterwards. */
export function loginHref(location: GuardLocation): string {
  return `${GUARD_PATHS.login}?returnUrl=${encodeURIComponent(location.href)}`;
}

/** Logged in, or off to log in (and back here afterwards). Returns the identity. */
export function requireSession(context: GuardContext, location: GuardLocation): Identity {
  const { session } = context;
  if (session.status !== 'authenticated' || !session.identity) {
    throw redirect({ href: loginHref(location) });
  }
  return session.identity;
}

/** Logged in with one of `roles` (`roleGuard`). */
export function requireRole(
  context: GuardContext,
  location: GuardLocation,
  roles: readonly RoleType[],
): Identity {
  const identity = requireSession(context, location);
  if (!hasRole(identity, roles)) {
    throw redirect({ href: GUARD_PATHS.unauthorized });
  }
  return identity;
}

/** Logged in as ADMIN at GLOBAL scope (`globalAdminGuard`). */
export function requireGlobalAdmin(context: GuardContext, location: GuardLocation): Identity {
  const identity = requireSession(context, location);
  if (!context.session.isGlobalAdmin) {
    throw redirect({ href: GUARD_PATHS.unauthorized });
  }
  return identity;
}

/** A GLOBAL admin in `admin` view mode, who is let through without asking (as in Angular). */
function adminShortcut(session: SessionContextValue): boolean {
  return session.isGlobalAdmin && session.scope.viewMode !== 'scoped';
}

/** `/me/access` at the selected node, loaded if it is not in the cache yet. */
export function ensureAccess(context: GuardContext, identity: Identity): Promise<Access> {
  const { session, queryClient } = context;
  return queryClient.ensureQueryData(
    accessQueryOptions(session.platform.client, identity.userId, session.scope.current?.id ?? null),
  );
}

/** Every one of `permissions`, effective at the selected node (`capabilitiesGuard`). */
export async function requirePermissions(
  context: GuardContext,
  location: GuardLocation,
  permissions: readonly string[],
): Promise<void> {
  const identity = requireSession(context, location);
  if (permissions.length === 0 || adminShortcut(context.session)) {
    return;
  }
  const access = await ensureAccess(context, identity);
  if (!permissions.every(permission => access.permissions.includes(permission))) {
    throw redirect({ href: GUARD_PATHS.unauthorized });
  }
}

/**
 * The tenant has bought `feature` and no node on the path switched it off.
 *
 * Skipped for GLOBAL admins in either view mode: their `/me/access` entitlements are the union
 * over every tenant, not the selected node's, so it cannot answer for them. Feature availability
 * (which honours `scoped`) does.
 */
export async function requireEntitlement(
  context: GuardContext,
  location: GuardLocation,
  feature: string,
): Promise<void> {
  const identity = requireSession(context, location);
  if (context.session.isGlobalAdmin) {
    return;
  }
  const access = await ensureAccess(context, identity);
  if (!access.entitlements.includes(feature)) {
    throw redirect({
      href: unavailableHref({ available: false, reason: 'tenant_disabled' }, feature),
    });
  }
}

function unavailableHref(availability: FeatureAvailability, feature: string): string {
  const reason = availability.available ? 'unknown' : availability.reason;
  const params = new URLSearchParams({ feature, reason });
  return `${GUARD_PATHS.unavailable}?${params}`;
}

/**
 * The feature's resolved configuration says it is available at the selected node, in the
 * current view mode. New: Angular only logged a warning and rendered the screen anyway.
 */
export async function requireFeatureAvailable(
  context: GuardContext,
  location: GuardLocation,
  feature: string,
): Promise<void> {
  const identity = requireSession(context, location);
  const { session, queryClient } = context;
  const orgNodeId = session.scope.current?.id ?? null;
  const availability: FeatureAvailability = orgNodeId
    ? featureAvailability(
        await queryClient.ensureQueryData(
          featureConfigurationQueryOptions(session.platform.client.json, feature, {
            userId: identity.userId,
            orgNodeId,
            viewMode: session.scope.viewMode,
          }),
        ),
      )
    : // Fail closed: no node, no answer.
      { available: false, reason: 'unknown' };
  if (!availability.available) {
    throw redirect({ href: unavailableHref(availability, feature) });
  }
}

export interface FeatureRequirement {
  /** The feature key the route renders (its `staticData.featureKey`). */
  feature: string;
  /** Permissions the page needs, e.g. `['lead:read']`. */
  permissions?: readonly string[];
  /** Also check `/me/access` entitlements. On by default; core features pass it anyway. */
  entitlement?: boolean;
}

/**
 * Everything a feature page needs: a session, its permissions, its entitlement, and the
 * feature available at the selected node — checked in that order, each with its own redirect.
 */
export async function requireFeature(
  context: GuardContext,
  location: GuardLocation,
  requirement: FeatureRequirement,
): Promise<void> {
  requireSession(context, location);
  await requirePermissions(context, location, requirement.permissions ?? []);
  if (requirement.entitlement !== false) {
    await requireEntitlement(context, location, requirement.feature);
  }
  await requireFeatureAvailable(context, location, requirement.feature);
}
